import { GAME_NAME, IS_LITE } from '../core/edition';
import {
  LIMITS,
  initialSettings,
  applyVisualPreset,
  type Settings,
  type QualityName,
  type PresetName,
} from '../core/config';
import { type Action, Input, type Tool } from '../input/actions';

const svg = (paths: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
const icons = {
  c4: svg(
    '<rect x="4.5" y="8" width="15" height="12" rx="2.5"/><path d="M8 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3M8.5 12v4m3.5-4v4m3.5-4v4"/>',
  ),
  detonate: svg('<path d="m13.5 2-9 12h7l-1 8 9-12h-7l1-8Z"/>'),
  rebuild: svg('<path d="M4 9a8.5 8.5 0 1 1-.2 6M4 3v6h6"/>'),
  settings: svg('<path d="M4 6h16M4 12h16M4 18h16"/><path d="M8 4v4m8 2v4M9 16v4"/>'),
  close: svg('<path d="m6 6 12 12M18 6 6 18"/>'),
};
const TOOL_INFO: Record<Tool, { name: string; hint: string; icon: string; key: string }> = {
  airstrike: {
    name: 'Airstrike',
    key: '3',
    hint: 'Aim the strip with the camera · hold one second for an eight-bomb pass',
    icon: svg(
      '<path d="m2 8 8-3 2-3 2 3 8 3-8-1-2 3-2-3ZM5 14v6m7-7v7m7-6v6M3 18l2 2 2-2m3 0 2 2 2-2m3 0 2 2 2-2"/>',
    ),
  },
  c4: { name: 'C4', hint: 'Tap a surface to attach · use Detonate', icon: icons.c4, key: '1' },
  plane: {
    name: 'Plane Crash',
    hint: 'Preview cargo approach and impact area · hold one second to launch · release early to cancel',
    icon: svg('<path d="m2 14 8-4V4a2 2 0 0 1 4 0v6l8 4v2l-8-2v5l3 2H7l3-2v-5l-8 2Z"/>'),
    key: '2',
  },
};

export class HUD {
  root: HTMLElement;
  panel: HTMLDialogElement;
  toastNode: HTMLElement;
  toastUntil = 0;
  detonate: HTMLButtonElement;
  holdBar: HTMLElement;
  stats: HTMLElement;
  help: HTMLElement;
  private supportedAudio = true;
  onAction: (action: Action) => void = () => {};
  onTool: (tool: Tool) => void = () => {};
  onSettings: () => void = () => {};
  onSkip: () => void = () => {};

  constructor(
    readonly input: Input,
    readonly settings: Settings,
  ) {
    this.root = document.createElement('div');
    this.root.id = 'hud';
    this.root.innerHTML = `
      <div class="edition-mark glass"><span>${GAME_NAME}</span>${IS_LITE ? '<span class="lite-badge" aria-label="Lite edition">Lite</span>' : '<span class="edition-label">NORMAL</span>'}</div>
      <div class="camera-tools glass" aria-label="Camera controls">
        <button id="camera-home" aria-label="City overview" title="City overview">⌂</button>
        <button id="zoom-in" aria-label="Zoom in" title="Zoom in">+</button>
        <button id="zoom-out" aria-label="Zoom out" title="Zoom out">−</button>
        <button id="touch-mode" aria-pressed="false" title="Switch one-finger drag between pan and rotate">Pan</button>
      </div>
      <div class="top-actions" aria-label="City controls">
        <span id="city-power" class="glass" role="status" hidden></span>
        <button class="glass" id="invasion-start" hidden>Hostile Invasion</button>
        <button class="glass" id="invasion-cancel" hidden>Cancel invasion</button>
        <button class="glass rebuild-button" id="rebuild" title="Rebuild city (B)">${icons.rebuild}<span>Rebuild</span><kbd>B</kbd></button>
        <button class="glass square" id="settings-button" aria-label="Pause and settings" title="Pause and settings (P)">${icons.settings}</button>
      </div>
      <div id="toast" class="glass" role="status" aria-live="polite"></div>
      <div id="invasion-status" class="glass" role="status" hidden></div>
      <dialog id="invasion-confirm" aria-labelledby="invasion-heading">
        <h2 id="invasion-heading">Hostile Invasion</h2>
        <p>600 reinforced hostiles arrive at double transport pace with heavy weapons and bombing support. Garrison survivors and the 200-person Blackguard reserve defend the city while residents seek shelter.</p>
        <p>Cancel stops attacks and begins withdrawal. Damage and casualties remain. Rebuild restores the original city and friendly roster.</p>
        <div class="dialog-heading"><button id="invasion-confirm-back">Back</button><button id="invasion-confirm-start">Start invasion</button></div>
      </dialog>
      <div id="rebuild-banner" class="glass" hidden>
        <div><strong>Rebuilding</strong><span id="rebuild-status">Clearing the way.</span></div>
        <button id="skip">Skip</button><div id="rebuild-progress"></div>
      </div>
      <footer class="bottom-hud">
        <span id="hold-status" class="glass" hidden>Hold to release</span>
        <nav class="tool-tray glass" aria-label="Demolition tools">
          ${(['c4', 'plane', 'airstrike'] as Tool[]).map((tool) => `<button data-tool="${tool}" aria-pressed="${tool === 'c4'}" aria-label="${TOOL_INFO[tool].name}" title="${TOOL_INFO[tool].name} (${TOOL_INFO[tool].key}) · ${TOOL_INFO[tool].hint}"><span class="tool-content">${TOOL_INFO[tool].icon}<span>${TOOL_INFO[tool].name}</span><kbd>${TOOL_INFO[tool].key}</kbd></span>${tool === 'plane' ? '<span id="hold-progress"></span>' : ''}<span class="tool-tip" aria-hidden="true">${TOOL_INFO[tool].hint}</span></button>`).join('')}
          <button id="detonate" hidden title="Detonate placed charges (X)">${icons.detonate}<span>Detonate</span><span id="charge-count">0</span><kbd>X</kbd></button>
        </nav>
      </footer>
      <pre id="stats" class="glass" aria-label="Performance diagnostics" hidden></pre>
      <dialog id="settings-dialog" aria-labelledby="settings-heading">
        <div class="dialog-heading"><div><span class="eyebrow">PAUSED</span><h2 id="settings-heading">Settings</h2></div><button id="resume" aria-label="Resume game">${icons.close}</button></div>
        <button id="invasion-settings-cancel" hidden>Cancel invasion</button>
        <div class="settings-grid">
          <label>Graphics preset<select id="quality"><option value="low">Low</option><option value="medium">Balanced</option><option value="high">Maximum</option><option value="custom">Custom</option><option value="auto" hidden>Maximum · legacy auto</option></select></label>

          <label>Render scale <output id="scale-value"></output><input id="scale" type="range" min="0.5" max="1.5" step="0.05"/></label>
          <label>Shadow quality<select id="shadows"><option value="0">Off</option><option value="512">Low · 512</option><option value="1024">Medium · 1024</option><option value="2048">High · 2048</option></select></label>
          <label>Effects density<select id="effects"><option value="0">Low</option><option value="1">Medium</option><option value="2">High</option></select></label>
          <label>Debris detail<select id="detail"><option value="0">Large sections</option><option value="1">Balanced</option><option value="2">Detailed</option></select></label>
          <label>C4 footprint<select id="c4Footprint"><option value="1">Original area · 1×</option><option value="2.5">Local · 2.5×</option><option value="10">Large · 10×</option><option value="25">Very large · 25×</option></select></label>
          <label>Airstrike strength <output id="airstrikeStrength-value"></output><input id="airstrikeStrength" type="range" min="0" max="10" step="0.25"/></label>
          <label>C4 strength <output id="c4Strength-value"></output><input id="c4Strength" type="range" min="0.5" max="3" step="0.25"/></label>
          <label>Residents<select id="density"><option value="180">180 · Lite default</option><option value="220">220 · quiet</option><option value="600">600</option><option value="1000">1,000</option><option value="1200">1,200</option><option value="1600">1,600</option><option value="2000">2,000 · Maximum default</option></select></label>
          <label>Nearby detail <output id="viewDistance-value"></output><input id="viewDistance" type="range" min="90" max="360" step="15"/></label>
          <label>Scenery distance <output id="sceneryDistance-value"></output><input id="sceneryDistance" type="range" min="600" max="1800" step="100"/></label>
          <label>Master volume <output id="volume-value"></output><input id="volume" type="range" min="0" max="1" step="0.05"/></label>
          <label>Effects <output id="effectsVolume-value"></output><input id="effectsVolume" type="range" min="0" max="1" step="0.05"/></label>
          <label>Music <output id="musicVolume-value"></output><input id="musicVolume" type="range" min="0" max="1" step="0.05"/></label>
          <label>Ambience <output id="ambienceVolume-value"></output><input id="ambienceVolume" type="range" min="0" max="1" step="0.05"/></label>
          <label>Sirens <output id="sirenVolume-value"></output><input id="sirenVolume" type="range" min="0" max="1" step="0.05"/></label>
          <label class="check"><input id="sirenMuted" type="checkbox"/>Mute sirens</label>
          <label>Camera sensitivity <output id="sensitivity-value"></output><input id="sensitivity" type="range" min="0.3" max="2" step="0.1"/></label>
          <label class="check"><input id="adaptive" type="checkbox"/>Adaptive render scale</label>
          <label>Train minimum interval<select id="railHeadway"><option value="60">60 seconds</option><option value="90">90 seconds</option><option value="150">150 seconds</option></select></label>
          <label class="check"><input id="reduced" type="checkbox"/>Reduced effects</label>
          <label class="check"><input id="slow" type="checkbox"/>Slow motion · ⅓ speed</label>
        </div>
        <p class="setting-note">${IS_LITE ? 'Lite: compact central city, 180 residents, 48 road vehicles, simpler debris and no large military/transport simulation.' : 'Normal: full detailed city, 2,000 residents and the complete military and transport simulation.'} Rendering follows your display refresh. Airstrikes have no cooldown or concurrent-run cap. Strength 0× is an inert practice pass.</p>
        <p class="setting-note">Presets apply visual settings. Individual visual changes show Custom. Your preferences stay across visits and rebuilds.</p>
        <div class="settings-actions"><button id="settings-defaults">Restore default settings</button></div>
        <details id="audio-preview"><summary>Soundtrack, audio preview and credits</summary>
          <p id="audio-track" class="setting-note">Eight local instrumental tracks · audio starts after a gesture.</p>
          <div class="settings-actions"><button id="audio-next">Next track</button><button id="audio-record">Record 20 seconds of play</button><a href="/audio/credits.html" target="_blank" rel="noopener">Music and sound credits</a></div>
          <div class="settings-grid"><label>Sample preview<select id="audio-sample"><option value="blast">Explosion</option><option value="concrete">Masonry impact</option><option value="wood">Timber impact</option><option value="glass">Glass break</option><option value="metal">Metal impact</option><option value="door">Bus door</option><option value="footstep">Footsteps</option></select></label><button id="audio-listen">Play sample</button></div>
          <p class="setting-note">Preview plays audio while the city stays paused. Recording resumes play and downloads the actual limited stereo mix.</p>
        </details>
        <div class="settings-grid inspection-row"><label>Visit a district or crossing<select id="district-inspect"><option value="off">Choose a destination</option></select></label></div>
        <div class="settings-grid"><label>Interior peek<select id="interior-peek"><option value="off">Normal exterior</option></select></label><label>Resident inspector<select id="resident-inspect"><option value="off">Choose a resident</option></select></label></div>
        <div class="settings-grid"><label>Interior view<select id="interior-view"><option value="cutaway">Cutaway</option><option value="glass">Through glass</option></select></label><p class="setting-note">Street life uses simple pavement journeys. Distant civilians use coarse movement; offscreen civilians unload. Incident crews stay active.</p></div>
        <pre id="resident-info" class="setting-note" style="white-space:pre-wrap;max-height:140px;overflow:auto" hidden></pre>
        <div class="settings-grid"><label>Rail services<select id="rail-inspect"><option value="off">Visit a station</option></select></label><p id="rail-info" class="setting-note"></p></div>
        <div class="settings-grid"><label>Local buses<select id="bus-inspect"><option value="off">Visit a bus stop</option><option value="follow">Follow a city bus</option></select></label><p id="bus-info" class="setting-note"></p></div>
        <div class="settings-grid"><label>Deliveries<select id="delivery-inspect"><option value="off">Choose a courier</option></select></label><p id="delivery-info" class="setting-note"></p></div>
        <div class="settings-grid"><label>Airport and air support<select id="aviation-inspect"><option value="off">Visit an aviation service</option><option value="terminal">Terminal and gates</option><option value="apron">Aircraft stands</option><option value="runway">Runway and taxiways</option><option value="flight">Follow a scheduled flight</option><option value="air-medical">Follow medical air support</option><option value="air-police">Follow observation helicopter</option></select></label><p id="aviation-info" class="setting-note"></p></div>
        <div class="settings-actions"><button id="instant-reset">Instant reset</button><button id="overview">Overview <kbd>H</kbd></button><button id="help-toggle" aria-expanded="false" aria-controls="help">Controls</button></div>
        <div id="help" hidden>
          <dl><dt>Move</dt><dd>WASD / arrow keys · Shift to travel faster</dd><dt>Look</dt><dd>Right drag to orbit · middle or Shift drag to pan</dd><dt>Zoom</dt><dd>Scroll toward the cursor · H for region overview</dd><dt>Demolish</dt><dd>1 C4 · click to place · X detonate · 2 Plane Crash · hold to launch</dd><dt>Restore</dt><dd>B rebuild · Backspace instant reset</dd><dt>Other</dt><dd>P / Escape pause · F3 diagnostics</dd><dt>Touch</dt><dd>Tap to place C4 · hold to launch a test plane · drag to orbit · two fingers pan and pinch</dd></dl>
        </div>
        <p class="limits" id="limits">Up to ${LIMITS.charges} placed charges. Settings are kept for this session.</p>
        <button id="resume-large">Resume</button>
      </dialog>`;
    document.getElementById('app')!.append(this.root);
    this.panel = this.get('settings-dialog');
    this.toastNode = this.get('toast');
    this.detonate = this.get('detonate');
    this.holdBar = this.get('hold-progress');
    this.stats = this.get('stats');
    this.help = this.get('help');
    this.help.insertAdjacentHTML(
      'afterbegin',
      '<p>Touch: tap a surface to place C4. Drag to pan; use Rotate for one-finger orbit. Pinch with two fingers to zoom and slide to pan. Hold on a surface for one second to launch a plane or airstrike. All actions also have buttons.</p>',
    );

    this.root.addEventListener('pointerdown', () => this.input.cancel('interface'));
    this.root.addEventListener('keydown', (event) => {
      if (event.code === 'F3') {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) this.onAction('debug');
      } else if (event.code === 'KeyP' && this.panel.open) {
        event.preventDefault();
        event.stopPropagation();
        if (!event.repeat) this.onAction('pause');
      }
    });
    this.root.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('button') && !this.panel.open) this.input.canvas.focus({ preventScroll: true });
    });
    this.root.querySelectorAll<HTMLButtonElement>('.tool-tray [data-tool]').forEach((button) => {
      button.onclick = () => this.onTool(button.dataset.tool as Tool);
    });
    for (const [id, action] of Object.entries({
      rebuild: 'rebuild',
      overview: 'overview',
      'settings-button': 'pause',
      resume: 'pause',
      'resume-large': 'pause',
      'instant-reset': 'instant',
      detonate: 'detonate',
    }))
      this.get<HTMLButtonElement>(id).onclick = () => this.onAction(action as Action);
    this.get<HTMLButtonElement>('overview').onclick = () => {
      if (this.panel.open) this.onAction('pause');
      this.onAction('overview');
    };
    this.get<HTMLButtonElement>('skip').onclick = () => this.onSkip();
    this.get<HTMLButtonElement>('help-toggle').onclick = () => {
      this.help.hidden = !this.help.hidden;
      this.get('help-toggle').setAttribute('aria-expanded', String(!this.help.hidden));
    };
    this.panel.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.onAction('pause');
    });
    this.get('camera-home').onclick = () => this.onAction('overview');
    this.get('zoom-in').onclick = () => {
      this.input.zoom -= 0.3;
    };
    this.get('zoom-out').onclick = () => {
      this.input.zoom += 0.3;
    };
    this.get('touch-mode').onclick = () => {
      this.input.touchPan = !this.input.touchPan;
      this.get('touch-mode').textContent = this.input.touchPan ? 'Pan' : 'Rotate';
      this.get('touch-mode').setAttribute('aria-pressed', String(!this.input.touchPan));
    };
    this.bindSettings();
    this.selectTool('c4');
  }

  get<T extends HTMLElement = HTMLElement>(id: string): T {
    return this.root.querySelector(`#${id}`)! as T;
  }

  private bindSettings(): void {
    const ids = [
      'quality',
      'scale',
      'shadows',
      'effects',
      'detail',
      'density',
      'viewDistance',
      'sceneryDistance',
      'adaptive',
      'volume',
      'effectsVolume',
      'musicVolume',
      'ambienceVolume',
      'sirenVolume',
      'sirenMuted',
      'sensitivity',
      'reduced',
      'slow',
      'c4Footprint',
      'c4Strength',
      'airstrikeStrength',
      'railHeadway',
    ] as const;
    this.get<HTMLButtonElement>('settings-defaults').onclick = () => {
      Object.assign(this.settings, initialSettings());
      for (const id of ids) {
        const element = this.get<HTMLInputElement | HTMLSelectElement>(id);
        if (element instanceof HTMLInputElement && element.type === 'checkbox')
          element.checked = Boolean(this.settings[id]);
        else element.value = String(this.settings[id]);
      }
      this.outputs();
      this.onSettings();
    };
    for (const id of ids) {
      const element = this.get<HTMLInputElement | HTMLSelectElement>(id);
      if (element instanceof HTMLInputElement && element.type === 'checkbox')
        element.checked = Boolean(this.settings[id]);
      else {
        // Preserve an explicit pre-existing session value even if it is between presets.
        if (
          element instanceof HTMLSelectElement &&
          ![...element.options].some((option) => option.value === String(this.settings[id]))
        ) {
          element.add(new Option(String(this.settings[id]), String(this.settings[id])));
        }
        element.value = String(this.settings[id]);
      }
      element.addEventListener('input', () => {
        if (id === 'quality') {
          this.settings.quality = element.value as QualityName;
          if (['low', 'medium', 'high'].includes(element.value)) {
            applyVisualPreset(this.settings, element.value as PresetName);
            for (const key of [
              'scale',
              'shadows',
              'effects',
              'detail',
              'viewDistance',
              'sceneryDistance',
            ] as const)
              this.get<HTMLInputElement>(key).value = String(this.settings[key]);
          }
        } else if (id === 'adaptive' || id === 'reduced' || id === 'slow' || id === 'sirenMuted')
          this.settings[id] = (element as HTMLInputElement).checked;
        else Object.assign(this.settings, { [id]: Number(element.value) });
        if (['scale', 'shadows', 'effects', 'detail', 'viewDistance', 'sceneryDistance'].includes(id)) {
          this.settings.quality = 'custom';
          this.get<HTMLSelectElement>('quality').value = 'custom';
        }
        this.outputs();
        this.onSettings();
      });
    }
    this.outputs();
  }

  private outputs(): void {
    for (const id of [
      'scale',
      'volume',
      'effectsVolume',
      'musicVolume',
      'ambienceVolume',
      'sirenVolume',
      'sensitivity',
      'viewDistance',
      'sceneryDistance',
      'c4Strength',
      'airstrikeStrength',
    ] as const) {
      this.get(`${id}-value`).textContent =
        id === 'viewDistance' || id === 'sceneryDistance'
          ? `${this.settings[id]} m`
          : id === 'sensitivity' || id === 'c4Strength' || id === 'airstrikeStrength'
            ? `${this.settings[id].toFixed(1)}×`
            : id === 'volume' && !this.supportedAudio
              ? 'Unavailable'
              : `${Math.round(this.settings[id] * 100)}%`;
    }
  }

  audioAvailable(available: boolean): void {
    this.supportedAudio = available;
    this.get<HTMLInputElement>('volume').disabled = !available;
    this.outputs();
  }

  selectTool(tool: Tool): void {
    this.input.cancel();
    this.root.dataset.tool = tool;
    if (tool !== 'c4') this.root.querySelector(`.tool-tray [data-tool="${tool}"]`)?.append(this.holdBar);
    this.root.querySelectorAll<HTMLButtonElement>('.tool-tray [data-tool]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.tool === tool));
    });
  }

  toggleDebug(): void {
    this.stats.hidden = !this.stats.hidden;
  }

  pause(paused: boolean): void {
    if (paused && !this.panel.open) this.panel.showModal();
    else if (!paused && this.panel.open) {
      this.panel.close();
      this.input.canvas.focus({ preventScroll: true });
    }
  }

  message(text: string): void {
    this.toastNode.textContent = text;
    this.toastUntil = performance.now() + 2800;
    this.toastNode.classList.add('visible');
  }

  private rebuilding = false;
  rebuild(progress: number | null): void {
    const active = progress !== null;
    if (this.rebuilding !== active) {
      this.rebuilding = active;
      this.get('rebuild-banner').hidden = !active;
      this.root.dataset.rebuilding = String(active);
      this.root
        .querySelectorAll<HTMLButtonElement>('[data-tool],#detonate,#rebuild,#invasion-start')
        .forEach((button) => {
          button.disabled = active;
        });
    }
    if (progress !== null) {
      this.get('rebuild-progress').style.width = `${Math.min(100, Math.max(0, progress * 100))}%`;
      this.get('rebuild-status').textContent =
        progress < 0.2
          ? 'Clearing and preparing.'
          : progress < 0.64
            ? 'Frames and floors.'
            : progress < 0.88
              ? 'Facades and finishing.'
              : 'Returning to everyday life.';
    }
  }

  update(charges: number, hold: number, fps: number, metrics: Record<string, number | string>): void {
    this.detonate.hidden = charges === 0;
    this.get('charge-count').textContent = `${charges}/${LIMITS.charges}`;
    this.holdBar.style.width = `${Math.min(100, hold * 100)}%`;
    const selected = this.root.querySelector('[data-tool][aria-pressed="true"]');
    if (selected && this.holdBar.parentElement !== selected) selected.append(this.holdBar);
    this.get('hold-status').hidden = hold <= 0;
    this.get('hold-status').textContent = `Hold · ${Math.max(0, 1 - hold).toFixed(1)} s`;
    if (performance.now() > this.toastUntil) this.toastNode.classList.remove('visible');
    if (!this.stats.hidden)
      this.stats.textContent =
        `${Math.round(fps)} FPS · F3 to close\n\n` +
        Object.entries(metrics)
          .map(
            ([key, value]) =>
              `${key.padEnd(21)} ${typeof value === 'number' ? value.toFixed(value % 1 ? 2 : 0) : value}`,
          )
          .join('\n');
  }
}
